import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import AwardBadge from "@/components/AwardBadge";
import { CVC_LOGO } from "@/components/landing/constants";
import { ArrowLeft, ArrowRight, CaretRight, Mountains, MapPin } from "@phosphor-icons/react";

// The three strategic-axis dimensions ScaledEasy tags on every session
// (populated from the public feed — see /app/backend/services/approach_sessions.py).
const DIMENSIONS = [
  { key: "Improve Company Aim",         axis: "Strategy",  note: "Where the company is going — vision, market, offer clarity." },
  { key: "Reduce Dependence on Owner",  axis: "Structure", note: "How the company runs without you — roles, systems, cadences." },
  { key: "Improve Team Capability",     axis: "Talent",    note: "Who does the work — hiring, training, growing the bench." },
];

export default function Approach() {
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ sessions: [], total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/approach/sessions?page=${page}&size=10`)
      .then((r) => setData(r.data))
      .catch(() => setData({ sessions: [], total: 0, pages: 0 }))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  return (
    <div className="min-h-screen paper-grain text-cvc-ink bg-cvc-cream">
      {/* Slim top strip */}
      <nav data-testid="approach-nav" className="sticky top-0 z-30 bg-cvc-cream/85 backdrop-blur-md border-b border-cvc-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" data-testid="approach-nav-home" className="flex items-center gap-3">
            <img src={CVC_LOGO} alt="Cache Valley Consulting" className="h-9 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="font-body text-sm text-cvc-ink-soft hover:text-cvc-navy hidden sm:inline">← Home</Link>
            <Link to="/#qualify">
              <Button className="bg-cvc-red hover:bg-cvc-red-deep text-white rounded-full px-5 py-2.5 h-auto font-body">
                Check availability
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Editorial header */}
      <header className="max-w-4xl mx-auto px-6 lg:px-12 pt-16 pb-8">
        <p className="text-xs tracking-[0.3em] uppercase font-body font-semibold text-cvc-red flex items-center gap-2">
          <Mountains size={16} weight="duotone" /> The Approach · 100-step scale-up
        </p>
        <h1 data-testid="approach-h1" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-cvc-navy mt-6 leading-[1.05] font-medium">
          One hundred sessions between owner-operator and a business that runs without you.
        </h1>
        <p className="font-body text-lg text-cvc-ink-soft mt-6 leading-relaxed max-w-2xl">
          This is the exact 100-step scale-up program — the same arc our nationwide sister program{" "}
          <a href="https://www.scaledeasy.com/" target="_blank" rel="noopener noreferrer" className="text-cvc-navy underline underline-offset-4 decoration-cvc-border hover:decoration-cvc-navy">ScaledEasy.com</a>{" "}
          delivers by phone. Here in Cache Valley, Jonathan walks you through it{" "}
          <span className="italic">in person</span>, one on-site session at a time — floor walks,
          working sessions at your table, real accountability. No two clients move at the same
          pace, but the arc is the same for everyone.
        </p>
        <div className="mt-8 flex items-center gap-3 font-body text-sm text-cvc-ink-soft">
          <MapPin size={16} weight="duotone" /> Delivered locally in Logan, Utah by Jonathan Johnson.
        </div>
      </header>

      {/* Three strategic-axis dimensions (Strategy · Structure · Talent) */}
      <section className="max-w-6xl mx-auto px-6 lg:px-12 pt-6 pb-14">
        <div className="grid sm:grid-cols-3 gap-4">
          {DIMENSIONS.map((d) => (
            <div key={d.key} data-testid={`band-${d.axis.toLowerCase()}`} className="rounded-2xl border border-cvc-border bg-white p-5">
              <p className="text-[10px] tracking-[0.3em] uppercase font-body font-semibold text-cvc-red">{d.axis}</p>
              <h3 className="font-heading text-xl text-cvc-navy mt-2 leading-tight">{d.key}</h3>
              <p className="font-body text-[13px] text-cvc-ink-soft mt-2 leading-snug">{d.note}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Sessions list — 10 at a time */}
      <section className="max-w-4xl mx-auto px-6 lg:px-12 pb-14">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-heading text-2xl text-cvc-navy">
            Page {data.pages ? page : 0} of {data.pages || 10}
          </h2>
          <p className="font-body text-xs text-cvc-ink-soft tracking-wider">
            Showing {data.sessions.length ? (page - 1) * 10 + 1 : 0}–{Math.min(page * 10, data.total)} of {data.total || 100}
          </p>
        </div>

        <ol data-testid="approach-sessions-list" className="divide-y divide-cvc-border rounded-2xl bg-white border border-cvc-border overflow-hidden">
          {loading ? (
            <li className="p-6 font-body text-sm text-cvc-ink-soft">Loading sessions…</li>
          ) : data.sessions.length === 0 ? (
            <li className="p-6 font-body text-sm text-cvc-ink-soft">No sessions on this page.</li>
          ) : (
            data.sessions.map((s) => (
              <li
                key={s.number}
                data-testid={`session-${s.number}`}
                className="flex items-center gap-5 px-5 sm:px-7 py-5 hover:bg-cvc-parchment/40 transition-colors"
              >
                <span className="shrink-0 font-heading text-3xl text-cvc-red w-14 text-center leading-none">
                  {String(s.number).padStart(2, "0")}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`font-heading text-lg text-cvc-navy leading-snug ${s.is_placeholder ? "italic text-cvc-ink-soft" : ""}`}>
                    {s.title}
                  </p>
                  <p className="font-body text-[11px] uppercase tracking-[0.2em] text-cvc-ink-soft/70 mt-1">
                    {s.dimension || s.category}
                  </p>
                </div>
                <CaretRight size={20} weight="bold" className="text-cvc-border shrink-0" />
              </li>
            ))
          )}
        </ol>

        {/* Pagination */}
        <div className="mt-8 flex items-center justify-between">
          <Button
            data-testid="approach-prev"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded-full px-6 border-cvc-border"
          >
            <ArrowLeft size={16} weight="bold" className="mr-2" /> Previous 10
          </Button>
          <div className="flex items-center gap-1.5 flex-wrap justify-center">
            {Array.from({ length: data.pages || 10 }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                data-testid={`approach-page-${p}`}
                onClick={() => setPage(p)}
                className={`w-9 h-9 rounded-full font-body text-sm transition-colors ${p === page ? "bg-cvc-navy text-cvc-cream" : "bg-white border border-cvc-border text-cvc-ink hover:border-cvc-navy"}`}
              >
                {p}
              </button>
            ))}
          </div>
          <Button
            data-testid="approach-next"
            variant="outline"
            disabled={page >= (data.pages || 10)}
            onClick={() => setPage((p) => Math.min(data.pages || 10, p + 1))}
            className="rounded-full px-6 border-cvc-border"
          >
            Next 10 <ArrowRight size={16} weight="bold" className="ml-2" />
          </Button>
        </div>
      </section>

      {/* Business Scaling Assessment */}
      <section className="max-w-4xl mx-auto px-6 lg:px-12 pb-16" data-testid="approach-assessment-cta">
        <div className="rounded-2xl border border-cvc-border bg-white p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <p className="text-xs tracking-[0.3em] uppercase font-body font-semibold text-cvc-red">Business Scaling Assessment</p>
            <h2 className="font-heading text-2xl md:text-3xl text-cvc-navy mt-2 leading-tight">How ready is your business to grow without you?</h2>
            <p className="font-body text-cvc-ink-soft mt-3 leading-relaxed">
              87 statements across Strategy, Structure, and Talent, then a scorecard with your three weakest areas. Free, about 20 minutes.
            </p>
          </div>
          <Link to="/assessment?from=approach" data-testid="approach-assessment-start">
            <Button className="bg-cvc-navy hover:bg-cvc-navy-deep text-cvc-cream rounded-full px-7 py-5 h-auto font-body whitespace-nowrap">
              Take the assessment <ArrowRight size={16} weight="bold" className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-cvc-navy text-cvc-cream py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <AwardBadge size="sm" align="center" tone="cream" className="mx-auto" />
          <h2 className="font-heading text-3xl md:text-4xl mt-6 leading-tight">
            Ready to start Discovery #1 — locally, in person?
          </h2>
          <p className="font-body text-cvc-cream/85 mt-4 max-w-lg mx-auto">
            Submit a signup request. Jonathan reviews each case and confirms whether you're a fit
            to begin, or what preparation steps would set you up first.
          </p>
          <Link to="/#qualify">
            <Button className="mt-8 bg-cvc-red hover:bg-cvc-red-deep text-white rounded-full px-9 py-6 h-auto text-base font-body">
              Check availability <ArrowRight size={18} weight="bold" className="ml-2" />
            </Button>
          </Link>
          <p className="font-body text-xs text-cvc-cream/60 mt-8">
            Also delivered by phone, nationwide, at{" "}
            <a href="https://www.scaledeasy.com/" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-cvc-cream">ScaledEasy.com</a>.
          </p>
        </div>
      </section>
    </div>
  );
}
